import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles } from "lucide-react";
import { fireConfetti, scrollToSection, IMAGES } from "../lib/site";
import { useContent } from "../lib/content";

const STRIP_LAYOUT = [
  { cls: "float-soft", tilt: "-6deg", mt: "" },
  { cls: "float-soft-alt", tilt: "5deg", mt: "mt-16" },
];

const lines = [
  ["Capture", "the"],
  ["Magic", "of", "the"],
  ["Cayman", "Islands"],
];

const MaskedLine = ({ words, index, italicWord }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay: 0.25 + index * 0.16, ease: [0.22, 1, 0.36, 1] }}
    >
      {words.map((w, i) => (
        <span
          key={i}
          className={
            w === italicWord
              ? "font-serif italic text-[#c9006f] mr-[0.28em]"
              : "mr-[0.28em]"
          }
        >
          {w}
        </span>
      ))}
    </motion.span>
  </span>
);

const StripPhoto = ({ src, caption }) => (
  <div className="bg-white p-2 pb-1 rounded-sm">
    <img src={src} alt={caption} className="w-full h-36 sm:h-40 object-cover rounded-[2px]" />
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { heroStrips } = useContent();
  const strips = heroStrips || [];
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const leavesY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const stripY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen grain overflow-hidden bg-[#3c003a]"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: leavesY }}
        className="absolute inset-0 opacity-30"
        aria-hidden="true"
      >
        <img src={IMAGES.leaves} alt="" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#3c003a]/70 via-[#3c003a]/85 to-[#3c003a]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-36 lg:pt-44 pb-28 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 lg:gap-10 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#c9006f]/40 bg-white/5 backdrop-blur-md px-4 py-2 mb-8"
            data-testid="hero-badge"
          >
            <Sparkles size={14} className="text-[#c9006f]" />
            <span className="text-[#ffc4e4] text-xs tracking-[0.22em] uppercase font-semibold">
              Grand Cayman's Premier Photo Booth
            </span>
          </motion.div>

          <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-[-0.02em] mb-7">
            <MaskedLine words={lines[0]} index={0} />
            <MaskedLine words={lines[1]} index={1} italicWord="Magic" />
            <MaskedLine words={lines[2]} index={2} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="text-white/70 text-base md:text-lg leading-[1.7] max-w-xl mb-10"
          >
            Premium photo booth experiences for luxury weddings, corporate galas,
            and unforgettable island parties — crafted with white-glove service
            and studio-quality glamour.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={(e) => {
                fireConfetti(e);
                scrollToSection("#contact");
              }}
              data-testid="check-availability-btn"
              className="btn-gold rounded-full px-9 py-4 text-base font-bold tracking-wide"
            >
              Book Your GlamBooth
            </button>
            <button
              onClick={() => scrollToSection("#packages")}
              data-testid="view-packages-btn"
              className="btn-outline-glow rounded-full px-9 py-4 text-base font-semibold text-white border border-white/30 hover:text-[#ffc4e4] backdrop-blur-sm"
            >
              View Packages
            </button>
          </motion.div>
        </div>

        <motion.div
          style={{ y: stripY }}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden sm:flex justify-center items-center gap-6"
          data-testid="hero-photo-strips"
        >
          {strips.map((strip, si) => {
            const layout = STRIP_LAYOUT[si % STRIP_LAYOUT.length];
            return (
              <div
                key={strip.id || si}
                className={`${layout.cls} w-44 lg:w-52 bg-white p-2 rounded-md shadow-[0_30px_80px_rgba(0,0,0,0.45)] space-y-2 ${layout.mt}`}
                style={{ "--tilt": layout.tilt }}
                data-testid={`hero-strip-${si}`}
              >
                {strip.photos.map((src, i) => (
                  <StripPhoto key={i} src={src} caption={strip.caption} />
                ))}
                <p className="font-serif italic text-center text-[#91008d] text-sm py-1.5">
                  {strip.caption}
                </p>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
